import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { devLog, devError } from '../../utils/devLogger';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useLang } from '../../context/LangContext';
import { useUserAuth } from '../../context/UserAuthContext';
import { useBidContext } from '../../context/BidContext';
import { useAdditionalInfoCheck } from '../../hooks/useAdditionalInfoCheck';
import { getPreBidDetail } from '../../lib/api/user/userApi';
import { PreBidDetailData } from '../../lib/api/user/userApi.types';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import ImageCarousel from '../../components/ImageCarousel';
import AuctionInfo from '../../components/AuctionInfo';
import LotPrice from '../../components/LotPrice';
import LotDescription from '../../components/LotDescription';
import MyBid from '../../components/MyBid';
import BidHistory from '../../components/BidHistory';
import PrimaryButton from '../../components/PrimaryButton';
import LoginRequiredModal from '../../components/LoginRequiredModal';
import AdditionalInfoModal from '../../components/AdditionalInfoModal';
import NoDataContainer from '../../components/NoDataContainer';
import DataLoader from '../../components/DataLoader';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 140px; /* 고정 버튼 영역을 위한 하단 패딩 증가 */
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0;
  gap: 0; /* 각 컴포넌트에서 margin-bottom으로 간격 조절 */
`;

// 웹용 LOT 상세 컨텐츠 (좌우 배치)
const DesktopLotContent = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  
  > * {
    flex: 1;
    min-width: 0; /* flex 컨테이너에서 overflow 방지 */
    max-width: 50%; /* 각각 최대 50%로 제한 */
  }
`;

// 이미지 캐로셀 래퍼 (웹용)
const ImageCarouselWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  
  /* 데스크톱에서 높이 제한 및 비율 조정 */
  @media (min-width: 768px) {
    max-height: 500px; /* 최대 높이 제한 */
    
    /* ImageCarousel 컴포넌트 내부 스타일 오버라이드 */
    & > div > div {
      margin-bottom: 0; /* margin-bottom 제거 */
    }
    
    /* 카드 이미지 컨테이너 스타일 오버라이드 */
    .swiper-slide > div > div {
      aspect-ratio: 4/3 !important; /* 정사각형에서 4:3 비율로 변경 */
      max-height: 450px;
    }
  }
`;

// 웹용 입찰 섹션 컨테이너 (나의 입찰 + 입찰 내역)
const DesktopBidSection = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
`;

// 나의 입찰 래퍼 (고정 너비 + 웹에서 배경색)
const MyBidWrapper = styled.div<{ $hasBid: boolean }>`
  width: 350px;
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    background: ${props => props.$hasBid ? '#f8f5f0' : '#f3f6f6'};
    border-radius: 12px;
    padding: 20px;
    height: 120px; /* 웹에서 고정 높이 설정 */
  }
`;

// 입찰 내역 래퍼 (나머지 공간)
const BidHistoryWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

// 하단 네비게이션과 동일한 구조로 만든 고정 버튼 컨테이너
const FixedButtonContainer = styled.div`
  position: fixed;
  bottom: 75px; /* 앱에서 하단 네비게이션 위로 */
  left: 0;
  right: 0;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  z-index: 999; /* 하단 네비게이션(1000)보다 살짝 낮게 */
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1); /* 쉐도우 추가 */

  @media (min-width: 768px) {
    bottom: 0; /* 웹에서는 항상 바닥에 고정 */
    /* padding: 20px 0; */
    justify-content: center; /* 웹에서 중앙 정렬 */
  }
`;

// 웹용 컨테이너 (1200px 제한)
const WebContainer = styled.div`
  width: 100%;
  
  @media (min-width: 768px) {
    max-width: 1200px;
    display: flex;
    justify-content: flex-end; /* 1200px 컨테이너 내에서 오른쪽 정렬 */
    padding: 0 24px;
  }
`;

// 버튼 래퍼
const ButtonWrapper = styled.div`
  width: 100%;

  @media (min-width: 768px) {
    width: 390px;
    max-width: 390px;
  }
`;

const preBidTexts = {
  ko: {
    title: '사전 입찰',
    description: '사전 입찰 페이지입니다.\n곧 구현될 예정입니다.',
    bidButton: '입찰하기',
    bid: '입찰'
  },
  en: {
    title: 'Pre-Bid',
    description: 'Pre-bid page.\nComing soon.',
    bidButton: 'Place Bid',
    bid: 'Bid'
  }
};

function PreBid() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { lang } = useLang();
  const { user, isLoggedIn } = useUserAuth(); // 현재 사용자 정보와 로그인 상태 가져오기
  const { setCurrentLotData } = useBidContext(); // Context 추가
  const texts = preBidTexts[lang];

  // 추가 정보 체크 훅
  const bidRequestInfoCheck = useAdditionalInfoCheck({
    onNavigate: () => navigate('/bid-request')
  });

  // 상태 관리
  const [lotData, setLotData] = useState<PreBidDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // 좋아요 상태 업데이트 함수
  const handleFavoriteUpdate = (newLikedUserIndexes: number[]) => {
    if (lotData) {
      setLotData({
        ...lotData,
        likedUserIndexes: newLikedUserIndexes
      });
    }
  };

  // LotDetail에서 전달받은 데이터 (fallback)
  const { selectedItem } = location.state || {};

  // API 호출
  useEffect(() => {
    const fetchLotData = async () => {
      if (!id) {
        setError('LOT ID가 없습니다.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        devLog('🔍 [PreBid] getPreBidDetail 호출:', id);
        const response = await getPreBidDetail(id);
        devLog('🔍 [PreBid] API 응답:', response.data);
        
        if (response.data.statusCode === 200 && response.data.data) {
          const lotData: any = response.data.data; // LOT 데이터가 직접 반환됨
          
          // API 응답을 PreBidDetailData 형식에 맞게 변환
          const transformedLotData: PreBidDetailData = {
            index: lotData.index,
            code: lotData.code || '', // 서버에서 받아온 LOT 코드
            name: lotData.name, // API에서 name 필드로 직접 반환
            description: lotData.description || '',
            images: lotData.images || [],
            startPrice: lotData.startPrice || 0,
            exchangeRate: lotData.exchangeRate || 1400,
            startPriceUsd: lotData.startPriceUsd || 0,
            recommendNo: lotData.recommendNo || null,
            likedUserIndexes: lotData.likedUserIndexes || [],
            likeCount: lotData.likeCount || 0,
            createBy: lotData.createBy || '',
            updateBy: lotData.updateBy || '',
            createAt: lotData.createAt || '',
            updateAt: lotData.updateAt || '',
            category: lotData.category || { code: '', name: '', krName: '' },
            subCategory: lotData.subCategory || { code: '', name: '', krName: '' },
            auctioneer: {
              index: lotData.auctioneer?.index || 0,
              name: lotData.auctioneer?.name || '',
              description: lotData.auctioneer?.description || '',
              profilePic: lotData.auctioneer?.profilePic || '',
              term: lotData.auctioneer?.term
            },
            auction: {
              index: lotData.auction?.index || 0,
              name: lotData.auction?.name || '',
              description: lotData.auction?.description || '',
              url: lotData.auction?.url || '',
              auctionLotNo: lotData.auction?.auctionLotNo || 0,
              preBidStartAt: lotData.auction?.preBidStartAt || '',
              preBidEndAt: lotData.auction?.preBidEndAt || '',
              auctionStartAt: lotData.auction?.auctionStartAt || '',
              auctionEndAt: lotData.auction?.auctionEndAt || '',
              paymentDueAt: lotData.auction?.paymentDueAt || '',
              auctionFeeRate: lotData.auction?.auctionFeeRate || 0,
              platformFeeRate: lotData.auction?.platformFeeRate || 0,
              createAt: lotData.auction?.createAt || '',
              updateAt: lotData.auction?.updateAt || null,
              biddingRanges: lotData.auction?.biddingRanges || []
            },
            preBids: lotData.preBids || []
          };
          
          setLotData(transformedLotData);
          setCurrentLotData(transformedLotData); // Context에 변환된 데이터 저장
          devLog('PreBid 변환된 데이터:', transformedLotData);
        } else {
          setError(response.data.message || '데이터를 불러올 수 없습니다.');
        }
      } catch (err) {
        devError('LOT 상세 정보 조회 실패:', err);
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLotData();
  }, [id, setCurrentLotData]);

  // 입찰 신청 페이지로 이동 (state 제거 - Context 사용)
  const handleBidRequest = () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    // 로그인은 되어 있지만 추가 정보가 필요한 경우
    if (user?.needMoreInfo) {
      bidRequestInfoCheck.checkAndNavigate();
      return;
    }

    navigate('/bid-request');
  };

  // 모달 관련 핸들러들
  const handleCloseModal = () => {
    setShowLoginModal(false);
  };

  const handleLoginFromModal = () => {
    setShowLoginModal(false);
    navigate('/login');
  };

  // 좋아요 기능을 위한 추가정보 모달 처리
  const handleAdditionalInfoRequired = () => {
    bidRequestInfoCheck.checkAndNavigate();
  };

  // 최소 입찰가 계산 함수 (BidRequest와 동일한 로직)
  const calculateMinimumBidPrice = useCallback(() => {
    if (!lotData?.auction?.biddingRanges) {
      return null;
    }

    // preBids에서 가장 높은 금액 찾기, 없으면 startPrice 사용
    let maxBidPrice = 0;
    
    if (lotData.preBids && lotData.preBids.length > 0) {
      // preBids가 있는 경우: preBids에서 가장 높은 금액 사용
      maxBidPrice = lotData.preBids.reduce((max, bid) => {
        return Math.max(max, bid.price);
      }, 0);
    } else {
      // preBids가 없는 경우: startPrice를 기준으로 사용
      maxBidPrice = lotData.startPrice || 0;
    }

    // 해당 금액이 속하는 구간 찾기
    const biddingRanges = lotData.auction.biddingRanges;
    let currentRange = biddingRanges.find(range => {
      const minAmount = range.minBidAmount;
      const maxAmount = range.maxBidAmount;
      
      return maxBidPrice >= minAmount && (maxAmount === null || maxBidPrice < maxAmount);
    });

    // 구간을 찾지 못했거나 maxBidPrice가 구간의 최대값과 같은 경우, 다음 구간을 찾기
    if (!currentRange) {
      // maxBidPrice가 어떤 구간의 maxAmount와 정확히 일치하는 경우 다음 구간 찾기
      const rangeIndex = biddingRanges.findIndex(range => 
        range.maxBidAmount !== null && maxBidPrice === range.maxBidAmount
      );
      
      if (rangeIndex !== -1 && rangeIndex + 1 < biddingRanges.length) {
        // 다음 구간이 존재하면 사용
        currentRange = biddingRanges[rangeIndex + 1];
      } else {
        // 마지막 구간이거나 구간을 찾을 수 없는 경우
        currentRange = biddingRanges[biddingRanges.length - 1];
      }
    }

    if (!currentRange) {
      // 방어적 코딩: 가장 마지막 구간을 사용
      currentRange = biddingRanges[biddingRanges.length - 1];
      
      if (!currentRange) {
        // 구간이 아예 없는 경우 기본값 반환
        return maxBidPrice + 1000;
      }
    }

    // 최소 입찰가: 최대 금액 + rate
    const minimumBidPrice = maxBidPrice + currentRange.rate;

    return minimumBidPrice;
  }, [lotData]);

  // 버튼 텍스트 (통화 코드 포함 + 입찰)
  const bidButtonText = useMemo(() => {
    const minimumBidPrice = calculateMinimumBidPrice();
    
    if (!minimumBidPrice) {
      return texts.bidButton; // 기본 버튼 텍스트
    }

    // 언어별 통화 표시
    const exchangeRate = lotData?.exchangeRate || 1409.09;

    if (lang === 'ko') {
      return `KRW ${minimumBidPrice.toLocaleString()} ${texts.bid}`;
    } else {
      const usdPrice = Math.round((minimumBidPrice / exchangeRate) * 100) / 100;
      return `USD ${usdPrice.toFixed(2)} ${texts.bid}`;
    }
  }, [calculateMinimumBidPrice, texts.bidButton, texts.bid, lang, lotData?.exchangeRate]);

  // MyBid 데이터 준비
  const getMyBidData = () => {
    const myBid = lotData?.preBids?.find(bid => bid.user.index === user?.index);
    devLog('MyBid 디버깅:', {
      user,
      userIndex: user?.index,
      preBids: lotData?.preBids,
      myBid,
      bidAmount: myBid?.price,
      bidNumber: myBid?.user.code
    });
    
    return {
      bidAmount: myBid?.price,
      bidNumber: myBid?.user.code,
      hasBid: !!myBid
    };
  };

  // AuctionInfo에 전달할 데이터 변환
  const getAuctionData = () => {
    if (lotData) {
      // API 응답 데이터 사용 - PreBid 페이지에서는 사전입찰 시간 정보 사용
      return {
        auctionHouse: lotData.auctioneer?.name || '',
        auctioneerIndex: lotData.auctioneer?.index || 0, // auctioneerIndex 추가
        auction: lotData.auction?.name || '',
        lotName: lotData.name || '',
        index: lotData.index || 0, // 서버에서 받아온 index (화면에서는 LOT.{index}로 표시)
        code: lotData.code || '', // 서버에서 받아온 code (예: "LOT001")
        preBidStartTime: lotData.auction?.preBidStartAt || '',
        preBidEndTime: lotData.auction?.preBidEndAt || '',
        liveStartTime: lotData.auction?.auctionStartAt || '',
        liveEndTime: lotData.auction?.auctionEndAt || '',
        currentPrice: lotData.startPrice || 0, // API에는 currentPrice가 없으므로 startPrice 사용
        startPrice: lotData.startPrice || 0,
        liveStartPrice: lotData.startPrice || 0,
        artist: '', // API 응답에 artist 정보가 없음
        year: 0, // API 응답에 year 정보가 없음
        material: '', // API 응답에 material 정보가 없음
        size: '', // API 응답에 size 정보가 없음
        category: lotData.category?.krName || lotData.category?.name || '',
        subCategory: lotData.subCategory?.krName || lotData.subCategory?.name || '',
        estimateMin: 0, // API 응답에 estimate 정보가 없음
        estimateMax: 0, // API 응답에 estimate 정보가 없음
        exchangeRate: lotData.exchangeRate || 0,
        startPriceUsd: lotData.startPriceUsd || 0,
        description: lotData.description || '',
        images: lotData.images || [],
        likeCount: lotData.likeCount || 0,
        recommendNo: lotData.recommendNo || 0,
      };
    }
    
    // Fallback: 기존 selectedItem 사용
    if (!selectedItem) return undefined;
    
    return {
      auctionHouse: selectedItem.auctionHouse,
      auction: selectedItem.auction,
      lotName: selectedItem.lotName,
      preBidStartTime: '', // selectedItem에는 사전입찰 정보가 없음
      preBidEndTime: '',
      liveStartTime: selectedItem.liveTime,
      liveEndTime: selectedItem.auctionEndDate ? 
        new Date(selectedItem.auctionEndDate.replace(/(\d{2})\.(\d{2})\.(\d{2}) (\d{2}):(\d{2})/, '20$3-$2-$1T$4:$5:00')).toISOString() :
        selectedItem.liveTime,
      currentPrice: parseInt(selectedItem.startingPrice?.replace(/,/g, '') || '0'),
      startPrice: parseInt(selectedItem.startingPrice?.replace(/,/g, '') || '0'),
      liveStartPrice: parseInt(selectedItem.startingPrice?.replace(/,/g, '') || '0')
    };
  };

  // 이미지 URL 처리 함수 (RecommendationSection과 동일)
  const getImageSrc = (imageUrl?: string | null) => {
    if (!imageUrl || imageUrl.trim() === "") {
      return "/common/nodata.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (imageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
    }
    
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
  };

  // ImageCarousel에 전달할 이미지 데이터 준비
  const getImageData = () => {
    if (lotData?.images && lotData.images.length > 0) {
      return lotData.images.map((imageUrl, index) => getImageSrc(imageUrl));
    }
    return [];
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <MainLayout currentPage="home">
        <CenteredLayout>
          <ProjectAppbar 
            currentPage="home"
            title={texts.title}
            showBackButton={true}
            onBack={() => navigate(-1)}
          />
          <Container>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 'calc(100vh - 120px)',
              width: '100%'
            }}>
              <DataLoader />
            </div>
          </Container>
        </CenteredLayout>
      </MainLayout>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <MainLayout currentPage="home">
        <CenteredLayout>
          <ProjectAppbar 
            currentPage="home"
            title={texts.title}
            showBackButton={true}
            onBack={() => navigate(-1)}
          />
          <Container>
            <NoDataContainer 
              message={error}
              imageSize={80}
            />
          </Container>
        </CenteredLayout>
      </MainLayout>
    );
  }



  const MobileLayout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={texts.title}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <Container>
          <ContentContainer>
            <ImageCarousel 
              images={getImageData()}
              lotId={lotData?.index}
              likedUserIndexes={lotData?.likedUserIndexes}
              onFavoriteUpdate={handleFavoriteUpdate}
              onLoginRequired={() => setShowLoginModal(true)}
              onAdditionalInfoRequired={handleAdditionalInfoRequired}
            />
            <AuctionInfo 
              auctionData={getAuctionData()} 
              exchangeRate={lotData?.exchangeRate || 1300}
            />
            <LotPrice 
              priceValue={lotData?.startPrice || 0}
              exchangeRate={lotData?.exchangeRate || 1300}
            />
            <LotDescription 
              description={lotData?.description || ''}
            />
            <MyBid 
              bidAmount={getMyBidData().bidAmount}
              bidNumber={getMyBidData().bidNumber}
              hasBid={getMyBidData().hasBid}
              exchangeRate={lotData?.exchangeRate || 1300}
            />
            <BidHistory 
              bidHistory={lotData?.preBids?.map((bid) => ({
                bidNumber: bid.user.code,
                amount: bid.price,
                isMyBid: bid.user.index === user?.index
              })) || []}
              exchangeRate={lotData?.exchangeRate || 1300}
            />
          </ContentContainer>
          
          {/* 고정 버튼 영역 */}
          <FixedButtonContainer>
            <WebContainer>
              <ButtonWrapper>
                <PrimaryButton onClick={handleBidRequest}>
                  {bidButtonText}
                </PrimaryButton>
              </ButtonWrapper>
            </WebContainer>
          </FixedButtonContainer>
        </Container>
      </CenteredLayout>
    </MainLayout>
  );

  const DesktopLayout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={texts.title}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <Container>
          <ContentContainer>
            {/* LOT 상세 섹션 - 웹에서는 맨 위에 배치, 항상 표시 */}
            {/* <DesktopLotTitle>{lotData?.name || 'LOT 상세'}</DesktopLotTitle> */}
            
            <DesktopLotContent>
              <ImageCarouselWrapper>
                <ImageCarousel 
                  images={getImageData()}
                  lotId={lotData?.index}
                  likedUserIndexes={lotData?.likedUserIndexes}
                  onFavoriteUpdate={handleFavoriteUpdate}
                  onLoginRequired={() => setShowLoginModal(true)}
                  onAdditionalInfoRequired={handleAdditionalInfoRequired}
                />
              </ImageCarouselWrapper>
              <AuctionInfo 
                auctionData={getAuctionData()} 
                exchangeRate={lotData?.exchangeRate || 1300}
              />
            </DesktopLotContent>
            
            <LotPrice 
              priceValue={lotData?.startPrice || 0}
              exchangeRate={lotData?.exchangeRate || 1300}
            />
            <LotDescription 
              description={lotData?.description || ''}
            />
            
            {/* 웹용 입찰 섹션 - 나의 입찰과 입찰 내역을 같은 행에 배치 */}
            <DesktopBidSection>
              <BidHistoryWrapper>
                <BidHistory 
                  bidHistory={lotData?.preBids?.map((bid) => ({
                    bidNumber: bid.user.code,
                    amount: bid.price,
                    isMyBid: bid.user.index === user?.index
                  })) || []}
                  exchangeRate={lotData?.exchangeRate || 1300}
                />
              </BidHistoryWrapper>
              <MyBidWrapper $hasBid={getMyBidData().hasBid}>
                <MyBid 
                  bidAmount={getMyBidData().bidAmount}
                  bidNumber={getMyBidData().bidNumber}
                  hasBid={getMyBidData().hasBid}
                  exchangeRate={lotData?.exchangeRate || 1300}
                  isWeb={true}
                />
              </MyBidWrapper>
            </DesktopBidSection>
          </ContentContainer>
          
          {/* 고정 버튼 영역 */}
          <FixedButtonContainer>
            <WebContainer>
              <ButtonWrapper>
                <PrimaryButton onClick={handleBidRequest}>
                  {bidButtonText}
                </PrimaryButton>
              </ButtonWrapper>
            </WebContainer>
          </FixedButtonContainer>
        </Container>
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <>
      <ResponsiveView
        mobileView={MobileLayout}
        desktopView={DesktopLayout}
      />
      
      {/* 로그인 모달 */}
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={handleCloseModal}
        onLogin={handleLoginFromModal}
      />

      {/* 추가 정보 입력 모달 */}
      <AdditionalInfoModal
        isOpen={bidRequestInfoCheck.showAdditionalInfoModal}
        onClose={bidRequestInfoCheck.handleCloseModal}
        onGoToAdditionalInfo={bidRequestInfoCheck.handleGoToAdditionalInfo}
      />
    </>
  );
}

export default PreBid;
